import { useState, useEffect } from 'react';
import { apiCall } from '../api/client';
import CollapsibleEditorCard from './CollapsibleEditorCard';
import GrammarDrillEditor from './GrammarDrillEditor';
import LessonPlannerChat from './LessonPlannerChat';
import { Field, Section, inputCls } from './LessonEditorFields';
import { defaultExerciseType, lessonLabel } from '../utils/lessons';

// Array <-> textarea helpers: every list field is one-per-line.
const linesToArr = (s) => s.split('\n').map(x => x.trim()).filter(Boolean);
const arrToLines = (a) => (a || []).join('\n');

// Each lesson type practices differently, so its exercises are a different kind:
// grammar translates authored prompts, reading answers questions about the passage,
// writing responds to open tasks (where "expected" is just optional sample answers).
// Must match EXERCISE_TYPES / defaultExerciseType in the backend's utils/levels.js.
const EXERCISE_KIND = {
  grammar: {
    type: 'translate',
    hint: (target, source) => `The learner translates each ${source} prompt into ${target} and can self-check against your accepted answers.`,
    promptPlaceholder: (target, source) => `Prompt (${source}), e.g. We are tired.`,
    expectedPlaceholder: 'Accepted answers, one per line',
  },
  reading: {
    type: 'comprehension',
    hint: (target) => `Questions about the passage above, asked in ${target}. The learner self-checks against your accepted answers.`,
    promptPlaceholder: () => 'Question about the passage, e.g. Où est Marie ?',
    expectedPlaceholder: 'Accepted answers, one per line',
  },
  writing: {
    type: 'open',
    hint: (target) => `Learners write freely in ${target}, then review their draft against your criteria. Samples are optional. AI feedback is available only in the admin tutor preview.`,
    promptPlaceholder: () => 'Writing task, e.g. Introduce yourself in two sentences.',
    expectedPlaceholder: 'Sample answers (optional), one per line',
  },
};

// Authoring editor for a grammar/reading/writing lesson level: its metadata (on the
// course doc) plus its content (explanation, passage, example bank — in the
// lessonContent container). Saves metadata via PATCH and content via PUT.
//
// The tutor's scope is NOT authored here. Vocabulary is derived from the course's
// drills, the learner's own study progress, and this lesson's visible content
// (examples, passage, exercise answers); grammar is the accumulated "concepts taught"
// of every lesson so far. The author only names what THIS lesson introduces — the
// closed-world tutor prompt treats everything else as untaught.
export default function LessonLevelEditor(props) {
  return props.level.type === 'grammar-drill'
    ? <GrammarDrillEditor {...props} />
    : <AuthoredLessonEditor {...props} />;
}

function AuthoredLessonEditor({ level, courseId, token, controls, onSaved, columns = [], collapsed, onToggle, precedingLevelCount }) {
  const [title, setTitle] = useState(level.title || '');
  // Which column is the language being taught (target) vs the native/reference language
  // (source). These label the example bank and the exercises, so a lesson isn't
  // hardcoded to one language pair.
  const [sourceColumn, setSourceColumn] = useState(level.sourceColumn ?? 0);
  const [targetColumn, setTargetColumn] = useState(level.targetColumn ?? (columns.length > 1 ? 1 : 0));
  const [objectives, setObjectives] = useState(arrToLines(level.objectives));
  const [concepts, setConcepts] = useState(arrToLines(level.introducedConcepts));
  const [dialectNotes, setDialectNotes] = useState(arrToLines(level.dialectNotes));
  const [exercises, setExercises] = useState(
    (level.exercises || []).map(e => ({ ...e, type: e.type || defaultExerciseType(level.type), prompt: e.prompt || '', expected: arrToLines(e.expected), criteria: arrToLines(e.criteria) }))
  );
  const [explanation, setExplanation] = useState('');
  const [passage, setPassage] = useState('');
  const [examples, setExamples] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);
  const [contentError, setContentError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoadingContent(true);
    setContentError('');
    apiCall('GET', `/courses/${courseId}/lessons/${level.id}`, null, token)
      .then(d => { if (!cancelled) { setExplanation(d.content?.explanation || ''); setPassage(d.content?.passage || ''); setExamples(d.content?.examples || []); } })
      .catch(err => { if (!cancelled) setContentError(err.message || 'Could not load lesson content.'); })
      .finally(() => { if (!cancelled) setLoadingContent(false); });
    return () => { cancelled = true; };
  }, [courseId, level.id, token, loadAttempt]);

  function updateExample(i, key, val) {
    setExamples(prev => prev.map((e, j) => (j === i ? { ...e, [key]: val } : e)));
  }
  function updateExercise(i, key, val) {
    setExercises(prev => prev.map((e, j) => (j === i ? { ...e, [key]: val } : e)));
  }

  async function handleSave() {
    setError('');
    setSaved(false);
    if (loadingContent || contentError) return;
    if (!linesToArr(objectives).length) { setError('Add at least one learning objective.'); return; }
    if (isReading && !passage.trim()) { setError('Add a reading passage.'); return; }
    if ((isReading || isWriting) && !exercises.length) { setError('Add at least one practice exercise.'); return; }
    if (exercises.some(e => !e.prompt.trim() || (!isWriting && !linesToArr(e.expected).length))) {
      setError('Every exercise needs a prompt and every self-check needs an accepted answer.'); return;
    }
    if (isWriting && exercises.some(e => !Number.isInteger(Number(e.minWords || 0)) || Number(e.minWords || 0) < 0 || Number(e.minWords || 0) > 500)) {
      setError('Minimum words must be a whole number between 0 and 500.'); return;
    }
    setSaving(true);
    try {
      const meta = {
        title: title.trim(),
        sourceColumn,
        targetColumn,
        objectives: linesToArr(objectives),
        introducedConcepts: linesToArr(concepts),
        dialectNotes: linesToArr(dialectNotes),
        exercises: exercises
          .filter(e => e.prompt.trim())
          .map(e => ({ type: exerciseKind.type, prompt: e.prompt.trim(), expected: linesToArr(e.expected),
            hint: e.hint || '', explanation: e.explanation || '',
            ...(isWriting ? { criteria: linesToArr(e.criteria || ''), minWords: Number(e.minWords || 0) } : {}),
          })),
      };
      const updated = await apiCall('PATCH', `/courses/${courseId}/levels/${level.id}`, meta, token);
      const cleanExamples = examples.map(e => ({ target: e.target ?? e.fr ?? '', source: e.source ?? e.en ?? '' }))
        .filter(e => e.target.trim() || e.source.trim());
      await apiCall('PUT', `/courses/${courseId}/levels/${level.id}/content`, { explanation, passage, examples: cleanExamples }, token);
      onSaved?.(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.message || 'Failed to save lesson.');
    } finally {
      setSaving(false);
    }
  }

  const colName = (i) => columns[i] ?? `Column ${i}`;
  const targetName = colName(targetColumn);
  const sourceName = colName(sourceColumn);
  const colOptions = columns.map((c, i) => ({ value: i, label: c || `Column ${i}` }));
  const isReading = level.type === 'reading';
  const isWriting = level.type === 'writing';
  const exerciseKind = EXERCISE_KIND[level.type] || EXERCISE_KIND.grammar;

  return (
    <CollapsibleEditorCard
      title={title || level.title}
      subtitle={`${lessonLabel(level.type)} lesson`}
      controls={controls}
      collapsed={collapsed}
      onToggle={onToggle}
    >

      {level.type === 'grammar' && <LessonPlannerChat
        courseId={courseId} lessonId={level.id} token={token} precedingLevelCount={precedingLevelCount}
        disabled={loadingContent || !!contentError}
        draft={{
          title, sourceColumn, targetColumn, objectives: linesToArr(objectives),
          introducedConcepts: linesToArr(concepts), dialectNotes: linesToArr(dialectNotes), explanation,
          examples: examples.map(e => ({ target: e.target ?? e.fr ?? '', source: e.source ?? e.en ?? '' })),
          exercises: exercises.map(e => ({ prompt: e.prompt, expected: linesToArr(e.expected), hint: e.hint || '', explanation: e.explanation || '' })),
        }}
      />}

      <Section
        step={1}
        title="Lesson basics"
        blurb="The title names this lesson on the course page. The languages determine which language learners practise and which language the tutor uses to explain."
      >
        <Field label="Title">
          <input value={title} onChange={e => setTitle(e.target.value)} className={inputCls} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Language being taught (target)" hint={isReading ? 'The language of the passage, comprehension questions, and learner answers.' : 'The language the learner writes their answers in.'}>
            <select value={targetColumn} onChange={e => setTargetColumn(Number(e.target.value))} className={`${inputCls} bg-white`}>
              {colOptions.map(o => (
                <option key={o.value} value={o.value} disabled={o.value === sourceColumn}>{o.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Native / reference language (source)" hint="Used for translations beneath examples and AI tutor explanations.">
            <select value={sourceColumn} onChange={e => setSourceColumn(Number(e.target.value))} className={`${inputCls} bg-white`}>
              {colOptions.map(o => (
                <option key={o.value} value={o.value} disabled={o.value === targetColumn}>{o.label}</option>
              ))}
            </select>
          </Field>
        </div>
      </Section>

      <Section
        step={2}
        title={isReading ? 'Reading material' : isWriting ? 'Writing preparation' : 'Teaching content'}
        blurb={isReading ? 'Give learners a text to read, then test their understanding with the comprehension questions below.' : isWriting ? 'Prepare learners for the writing tasks below with guidance and optional model sentences.' : 'Explain the new grammar before learners try the practice exercises.'}
      >
        <Field label="Objectives" hint={`One per line. ${isReading ? 'State what learners should understand from the text.' : isWriting ? 'State what learners should be able to express in their writing.' : 'State what learners should be able to do with this grammar.'} Shown to learners and sent to the tutor as lesson goals.`}>
          <textarea value={objectives} onChange={e => setObjectives(e.target.value)} rows={3} className={`${inputCls} resize-y`} />
        </Field>

        <Field label={isWriting ? 'Writing guidance (Markdown)' : isReading ? 'Reading instructions (Markdown)' : 'Explanation (Markdown)'} hint={isWriting ? 'Shown before the writing tasks. Use this for planning or composition advice. Task prompts, samples, and review criteria below are sent to the AI evaluator.' : isReading ? 'Shown before the passage. Use this to tell learners what to look for. The passage itself is sent to the tutor and comprehension evaluator.' : 'The grammar explanation learners read before practising. Use Markdown for headings, lists, and tables. The tutor receives the objectives and concepts below as its scope.'}>
          {loadingContent
            ? <p className="text-sm text-amber-400">Loading content…</p>
            : <textarea value={explanation} onChange={e => setExplanation(e.target.value)} rows={8} className={`${inputCls} font-mono resize-y`} />}
        </Field>

        {/* The text a reading lesson studies. Shown highlighted after the explanation;
            the comprehension questions and the tutor are grounded in it. */}
        {isReading && (
          <Field
            label={`Reading passage (${targetName}, Markdown)`}
            hint="The text the learner studies — shown in its own highlighted box. Use only words the course has taught by this point; the questions below should be answerable from it."
          >
            {loadingContent
              ? <p className="text-sm text-amber-400">Loading content…</p>
              : <textarea value={passage} onChange={e => setPassage(e.target.value)} rows={6} className={`${inputCls} resize-y`} />}
          </Field>
        )}

        {/* Example bank */}
        <Field label="Examples" hint={`Shown as cards under "Examples": the ${targetName} sentence with its ${sourceName} translation underneath.`}>
          <div className="space-y-2">
            {examples.map((ex, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input value={ex.target ?? ex.fr ?? ''} onChange={e => updateExample(i, 'target', e.target.value)} placeholder={targetName} className={`${inputCls} flex-1`} />
                <input value={ex.source ?? ex.en ?? ''} onChange={e => updateExample(i, 'source', e.target.value)} placeholder={sourceName} className={`${inputCls} flex-1`} />
                <button type="button" onClick={() => setExamples(prev => prev.filter((_, j) => j !== i))} aria-label="Remove example" className="text-amber-300 hover:text-red-500 text-lg leading-none px-1">×</button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setExamples(prev => [...prev, { target: '', source: '' }])} className="mt-2 text-sm font-medium text-amber-600 hover:text-amber-800">+ Add example</button>
        </Field>
      </Section>

      <Section
        step={3}
        title="Practice exercises"
        blurb={exerciseKind.hint(targetName, sourceName)}
      >
        <div className="space-y-3">
          {exercises.map((ex, i) => (
            <div key={i} className="border border-amber-100 rounded-lg p-3 space-y-2">
              <div className="flex gap-2 items-center">
                <input aria-label={`Exercise ${i + 1} prompt`} value={ex.prompt} onChange={e => updateExercise(i, 'prompt', e.target.value)} placeholder={exerciseKind.promptPlaceholder(targetName, sourceName)} className={`${inputCls} flex-1`} />
                <button type="button" onClick={() => setExercises(prev => prev.filter((_, j) => j !== i))} aria-label="Remove exercise" className="text-amber-300 hover:text-red-500 text-lg leading-none px-1">×</button>
              </div>
              <textarea aria-label={`Exercise ${i + 1} ${isWriting ? 'sample' : 'accepted'} answers`} value={ex.expected} onChange={e => updateExercise(i, 'expected', e.target.value)} rows={2} placeholder={exerciseKind.expectedPlaceholder} className={`${inputCls} resize-y`} />
              {isWriting ? <>
                <Field label={`Exercise ${i + 1} review criteria`} hint="One per line. Learners check their own draft against these before completing the task.">
                  <textarea value={ex.criteria || ''} onChange={e => updateExercise(i, 'criteria', e.target.value)} rows={3} placeholder="Use three present-tense verbs.\nDescribe where you live." className={inputCls} />
                </Field>
                <Field label={`Exercise ${i + 1} minimum words`} hint="Optional; 0 means no minimum.">
                  <input type="number" min="0" max="500" value={ex.minWords ?? 0} onChange={e => updateExercise(i, 'minWords', e.target.value)} className={inputCls} />
                </Field>
              </> : <>
                <Field label={`Exercise ${i + 1} hint`} hint="Optional; revealed when the learner asks for help.">
                  <input value={ex.hint || ''} onChange={e => updateExercise(i, 'hint', e.target.value)} className={inputCls} />
                </Field>
                <Field label={`Exercise ${i + 1} answer explanation`} hint={isReading ? 'Explain the answer and quote supporting evidence from the passage.' : 'Explain why the answer uses this form. Shown after checking.'}>
                  <textarea value={ex.explanation || ''} onChange={e => updateExercise(i, 'explanation', e.target.value)} rows={2} className={inputCls} />
                </Field>
              </>}
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setExercises(prev => [...prev, { type: exerciseKind.type, prompt: '', expected: '', criteria: '' }])} className="mt-2 text-sm font-medium text-amber-600 hover:text-amber-800">+ Add exercise</button>
      </Section>

      <Section
        step={4}
        title="AI tutor scope"
        blurb="Never shown to the learner. The tutor may only use what the course has taught by this lesson, and treats everything else as &quot;coming later&quot; — you only name what THIS lesson adds."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Grammar concepts taught in this lesson" hint={'One per line, e.g. "être — present tense" or "[pronoun] + être statements". These accumulate: the tutor may use every concept from this and earlier lessons, and nothing beyond them.'}>
            <textarea value={concepts} onChange={e => setConcepts(e.target.value)} rows={3} className={`${inputCls} resize-y`} />
          </Field>
          <Field label="Dialect notes" hint="One per line (optional). Variety-specific guidance for the tutor, e.g. prefer 'on' over 'nous'.">
            <textarea value={dialectNotes} onChange={e => setDialectNotes(e.target.value)} rows={3} className={`${inputCls} resize-y`} />
          </Field>
        </div>

        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-600">
          <span className="font-medium">Everything else is automatic.</span> The tutor's vocabulary
          is gathered from the course's drills, the words this learner has actually studied, and
          every {targetName} word in this lesson's examples{isReading ? ', passage,' : ''} and
          exercise answers. Its grammar is the concepts listed across lessons so far. Anything not
          taught yet, it declines to teach — no need to list what the learner doesn't know.
          Refreshes when the course is re-published.
        </div>
      </Section>

      {error && <p className="text-red-500 text-sm">{error}</p>}
      {contentError && <div role="alert" className="text-red-500 text-sm">
        {contentError} <button type="button" onClick={() => setLoadAttempt(n => n + 1)} className="underline">Retry loading content</button>
      </div>}

      <button
        onClick={handleSave}
        disabled={saving || loadingContent || !!contentError || !title.trim()}
        className="bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-sm font-medium px-5 py-2 rounded-lg transition"
      >
        {saved ? 'Saved ✓' : saving ? 'Saving…' : 'Save Lesson'}
      </button>
    </CollapsibleEditorCard>
  );
}
